import React from 'react';
import { TFunction } from 'i18next';
import {
  getCodeValidation,
  getFieldValidation,
  getOptionValidation,
} from '@lib/utils/validationUtils';
import _get from 'lodash/get';
import Input from '@lib/components/Input/Input';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import { GetLoadOptions } from '@lib/components/Select/types';
import AssetCounterFieldNames from '@lib/enums/fieldNames/assetCounterFieldNames';
import Select, { SelectProps } from '@lib/components/Select/Select';
import {
  getMeasureTypeOptions,
  getMeasureUnitOptionsByType,
} from '@lib/utils/measure';
import styles from '@lib/assets/styles/Form.module.scss';
import ProhibitLowerIndexThanPreviousField from 'components/ProhibitLowerIndexThanPreviousField/ProhibitLowerIndexThanPreviousField';

type Args = {
  getAssetsLoadOptions: GetLoadOptions;
  isEditForm?: boolean;
  t: TFunction<'translation', undefined>;
};

export default function getFormFields({ isEditForm, t }: Args): FieldItem[] {
  return [
    {
      name: AssetCounterFieldNames.Name,
      label: t('name'),
      element: Input,
      validation: getFieldValidation({ t, required: true }),
    },
    {
      name: AssetCounterFieldNames.Code,
      label: t('counter-code'),
      element: Input,
      validation: getCodeValidation({ t, max: 3, required: true }),
      componentProps: {
        disabled: isEditForm,
      },
    },
    {
      name: AssetCounterFieldNames.MeasureType,
      label: t('measurement'),
      element: Select,
      validation: getOptionValidation({ t, required: true }),
      componentProps: {
        options: getMeasureTypeOptions(),
        usePortal: true,
        isClearable: false,
        isSearchable: false,
        disabled: isEditForm,
      } as SelectProps,
      formItemClassName: styles.formColumn,
      fieldChangeCallBack: ({
        type,
        setValue,
        name,
      }: FieldChangeCallBackArgs) => {
        if (type === 'change' && name === AssetCounterFieldNames.MeasureType) {
          setValue(AssetCounterFieldNames.MeasureUnit, null, {
            shouldValidate: false,
          });
        }
      },
    },
    {
      name: AssetCounterFieldNames.MeasureUnit,
      label: t('unit'),
      element: Select,
      validation: getOptionValidation({ t, required: true }),
      componentProps: {
        usePortal: true,
        isClearable: false,
        isSearchable: false,
      } as SelectProps,
      getComponentProps: (values: Values) => {
        const type = _get(values, [
          AssetCounterFieldNames.MeasureType,
          'value',
        ]);
        const options = getMeasureUnitOptionsByType(type);
        return {
          options,
        };
      },
      formItemClassName: styles.formColumn,
      isDisabledFn: (values: Values, name?: string) => {
        if (isEditForm) return true;
        if (!name) return false;
        const depsValue = _get(values, [
          AssetCounterFieldNames.MeasureType,
          'value',
        ]);
        return depsValue === undefined;
      },
    },
    {
      name: AssetCounterFieldNames.ProhibitLowerIndexThanPreviousField,
      customRender: () => {
        return <ProhibitLowerIndexThanPreviousField />;
      },
    },
    {
      name: AssetCounterFieldNames.ProhibitLowerIndexThanPrevious,
    },
  ];
}
